import { extraQueryEquals, pickExtraQuery, type ExtraQuerySlice } from "@/components/optimizer/filters/extra-query";
import { orderOddsRange, parseAmericanBound } from "@/components/optimizer/odds-range";
import { canonicalComparisonBooks, type OptimizerQueryState } from "@/components/optimizer/query-state";
import type { PlatformType } from "@/components/optimizer/utils-v2";
import { isWeights, withKnownPreset } from "@/lib/pricing-model";
import { isBounded, isStringList } from "@/lib/saved-filters";

export const QUICK_FILTER_PREFIX = "quick-filter:";
export const MAX_SAVED_QUICK_FILTERS = 10;
export const MAX_QUICK_FILTER_NAME_LENGTH = 40;

const MAX_MIN_BOOKS = 10;
const MAX_MIN_LIQUIDITY = 1_000_000;
const ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

export type QuickFilterState = Pick<
  ExtraQuerySlice,
  | "minEV"
  | "minDataPoints"
  | "minLiquidity"
  | "minOddsToHit"
  | "maxOddsToHit"
  | "minOdds"
  | "maxOdds"
  | "requiredComparisonBooks"
  | "weights"
  | "preset"
  | "requiredBooks"
>;

export type SavedQuickFilter = {
  readonly id: string;
  readonly name: string;
  readonly createdAt: number;
  readonly state: QuickFilterState;
};

export type QuickFilterFields = {
  readonly minEV: boolean;
  readonly minLiquidity: boolean;
  readonly model: boolean;
};

type HiddenQuickFilterState = Pick<QuickFilterState, "minEV" | "minLiquidity" | "weights" | "preset" | "requiredBooks">;

const UNSAVED: HiddenQuickFilterState = { minEV: 0, minLiquidity: 0, weights: null, preset: null, requiredBooks: [] };

export function quickFilterStateOf(form: OptimizerQueryState, fields: QuickFilterFields): QuickFilterState {
  return withHiddenFields({
    minEV: Math.max(0, form.minEV),
    minDataPoints: form.minDataPoints,
    minLiquidity: form.minLiquidity,
    minOddsToHit: form.minOddsToHit,
    maxOddsToHit: form.maxOddsToHit,
    minOdds: form.minOdds,
    maxOdds: form.maxOdds,
    requiredComparisonBooks: [...form.requiredComparisonBooks],
    weights: form.weights,
    preset: form.preset,
    requiredBooks: form.requiredBooks,
  }, fields, UNSAVED);
}

export function withHiddenFields(state: QuickFilterState, fields: QuickFilterFields, base: HiddenQuickFilterState): QuickFilterState {
  const model = fields.model ? state : base;
  return {
    ...state,
    minEV: fields.minEV ? state.minEV : base.minEV,
    minLiquidity: fields.minLiquidity ? state.minLiquidity : base.minLiquidity,
    weights: model.weights === null ? null : { ...model.weights },
    preset: model.preset,
    requiredBooks: [...model.requiredBooks],
  };
}

export function isQuickFilterFields(value: unknown): value is QuickFilterFields {
  return isRecord(value) && typeof value.minEV === "boolean" && typeof value.minLiquidity === "boolean" && typeof value.model === "boolean";
}

export function quickFilterPatch(
  state: QuickFilterState,
  fields: QuickFilterFields,
  userPresetNames: readonly string[],
): Partial<QuickFilterState> {
  const patch: Partial<QuickFilterState> = {
    minDataPoints: state.minDataPoints,
    minOddsToHit: state.minOddsToHit,
    maxOddsToHit: state.maxOddsToHit,
    minOdds: state.minOdds,
    maxOdds: state.maxOdds,
    requiredComparisonBooks: [...state.requiredComparisonBooks],
  };
  if (fields.minEV) patch.minEV = state.minEV;
  if (fields.minLiquidity) patch.minLiquidity = state.minLiquidity;
  if (fields.model) {
    const model = withKnownPreset(state, userPresetNames);
    patch.weights = model.weights === null ? null : { ...model.weights };
    patch.preset = model.preset;
    patch.requiredBooks = [...model.requiredBooks];
  }
  return patch;
}

export function quickFilterActive(form: OptimizerQueryState, patch: Partial<QuickFilterState>, ownBook: PlatformType | null): boolean {
  const settled = (extra: ExtraQuerySlice): ExtraQuerySlice => ({
    ...extra,
    requiredComparisonBooks: extra.requiredComparisonBooks.filter((book) => book !== ownBook),
    requiredBooks: extra.requiredBooks.filter((book) => book !== ownBook).sort(),
  });
  const current = pickExtraQuery(form);
  return extraQueryEquals(settled({ ...current, ...patch }), settled(current));
}

export function sameQuickFilterName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function isQuickFilterId(value: unknown): value is string {
  return typeof value === "string" && ID_PATTERN.test(value);
}

export function parseSavedQuickFilter(value: unknown): SavedQuickFilter | null {
  if (!isRecord(value)) return null;
  if (!isQuickFilterId(value.id)) return null;
  if (typeof value.name !== "string") return null;
  const name = value.name.trim();
  if (name.length === 0 || name.length > MAX_QUICK_FILTER_NAME_LENGTH) return null;
  if (!Number.isSafeInteger(value.createdAt) || (value.createdAt as number) < 0) return null;
  const state = parseState(value.state);
  if (state === null) return null;
  return { id: value.id, name, createdAt: value.createdAt as number, state };
}

export function savedQuickFilterFromRow(filterName: string, value: unknown): SavedQuickFilter | null {
  if (!isRecord(value) || value.v !== 1) return null;
  const filter = parseSavedQuickFilter(value.state);
  return filter !== null && filterName === `${QUICK_FILTER_PREFIX}${filter.id}` ? filter : null;
}

function parseState(value: unknown): QuickFilterState | null {
  if (!isRecord(value)) return null;
  const { minEV, minDataPoints, minLiquidity, minOddsToHit, maxOddsToHit, minOdds, maxOdds, requiredComparisonBooks, weights, preset, requiredBooks } = value;
  if (!isBounded(minEV, 100)) return null;
  if (!isBounded(minDataPoints, MAX_MIN_BOOKS) || !Number.isInteger(minDataPoints)) return null;
  if (!isBounded(minLiquidity, MAX_MIN_LIQUIDITY) || !Number.isInteger(minLiquidity)) return null;
  if (!isBounded(minOddsToHit, 100) || !isBounded(maxOddsToHit, 100) || minOddsToHit > maxOddsToHit) return null;
  if (!isOdds(minOdds) || !isOdds(maxOdds) || orderOddsRange(minOdds, maxOdds).minOdds !== minOdds) return null;
  if (weights !== null && !isWeights(weights)) return null;
  if (preset !== null && (typeof preset !== "string" || preset.length === 0 || weights === null)) return null;
  if (!isStringList(requiredBooks)) return null;
  return {
    minEV,
    minDataPoints,
    minLiquidity,
    minOddsToHit,
    maxOddsToHit,
    minOdds,
    maxOdds,
    requiredComparisonBooks: canonicalComparisonBooks(requiredComparisonBooks),
    weights: weights === null ? null : { ...weights },
    preset,
    requiredBooks: [...requiredBooks],
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isOdds(value: unknown): value is number | null {
  return value === null || (typeof value === "number" && parseAmericanBound(value) === value);
}
